import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiUsers } from 'react-icons/fi';
import { adminAPI, materialsAPI } from '../../../services/api';
import StudentGroupPicker from '../../../components/StudentGroupPicker';
import CreateLessonPageBlocks from '../CreateLesson/CreateLessonPageBlocks';

const EditLessonMaterialPage = () => {
    const { materialId, lessonId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [material, setMaterial] = useState(null);

    const [visibility, setVisibility] = useState('all'); // all | students
    const [selectedStudentIds, setSelectedStudentIds] = useState([]);
    const [selectedGroupIds, setSelectedGroupIds] = useState([]);

    const [students, setStudents] = useState([]);
    const [loadingStudents, setLoadingStudents] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const res = await materialsAPI.getMaterial(materialId);
                setMaterial(res.data);

                setVisibility(res.data.visibility || 'all');
                setSelectedStudentIds(res.data.studentIds || []);
            } catch (e) {
                console.error(e);
                alert('Не удалось загрузить материал');
            } finally {
                setLoading(false);
            }
        };

        if (materialId) load();
    }, [materialId]);

    useEffect(() => {
        const loadStudents = async () => {
            try {
                setLoadingStudents(true);
                const res = await adminAPI.getStudents();
                setStudents(res.data.students || []);
            } catch (e) {
                console.error(e);
                alert('Не удалось загрузить учеников');
            } finally {
                setLoadingStudents(false);
            }
        };

        loadStudents();
    }, []);

    const toggleStudent = (studentId) => {
        setSelectedStudentIds((prev) => {
            const idNum = typeof studentId === 'number' ? studentId : parseInt(studentId, 10);
            if (prev.includes(idNum)) return prev.filter((id) => id !== idNum);
            return [...prev, idNum];
        });
    };

    const renderExtraParams = useMemo(() => {
        return (
            <div style={{ borderTop: '1px solid #eee', paddingTop: '20px' }}>
                <h3 style={{ marginBottom: '15px' }}>Кому доступен материал</h3>

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    <button
                        type="button"
                        onClick={() => setVisibility('all')}
                        style={{
                            padding: '10px 14px',
                            borderRadius: '8px',
                            border: visibility === 'all' ? '2px solid #52c41a' : '2px solid #e8e8e8',
                            background: visibility === 'all' ? '#f6ffed' : 'white',
                            cursor: 'pointer',
                            fontWeight: 600,
                            color: visibility === 'all' ? '#52c41a' : '#333'
                        }}
                    >
                        Всем
                    </button>

                    <button
                        type="button"
                        onClick={() => setVisibility('students')}
                        style={{
                            padding: '10px 14px',
                            borderRadius: '8px',
                            border: visibility === 'students' ? '2px solid #4a90e2' : '2px solid #e8e8e8',
                            background: visibility === 'students' ? '#f0f7ff' : 'white',
                            cursor: 'pointer',
                            fontWeight: 600,
                            color: visibility === 'students' ? '#4a90e2' : '#333'
                        }}
                    >
                        Выбранным
                    </button>
                </div>

                {visibility === 'students' && (
                    <div>
                        <StudentGroupPicker
                            selectedGroupIds={selectedGroupIds}
                            onChange={setSelectedGroupIds}
                        />

                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                            <FiUsers color="#4a90e2" />
                            <div style={{ fontSize: '14px', color: '#555' }}>
                                Или отметьте учеников по отдельности:
                            </div>
                        </div>

                        {loadingStudents ? (
                            <div style={{ fontSize: '13px', color: '#999' }}>Загрузка учеников...</div>
                        ) : (
                            <div style={{ maxHeight: '220px', overflow: 'auto', paddingRight: '8px' }}>
                                {students.map((s) => {
                                    const id = s.id;
                                    const fullName = [s.first_name, s.last_name].filter(Boolean).join(' ') || s.username;
                                    const checked = selectedStudentIds.includes(id);

                                    return (
                                        <label
                                            key={id}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                                padding: '8px 10px',
                                                borderRadius: '8px',
                                                marginBottom: '6px',
                                                background: checked ? '#e3f2fd' : '#fafafa',
                                                border: checked ? '1px solid #4a90e2' : '1px solid #eee',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggleStudent(id)}
                                            />
                                            <div style={{ fontWeight: 600, fontSize: '13px' }}>{fullName}</div>
                                        </label>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }, [visibility, students, selectedStudentIds, selectedGroupIds, loadingStudents]);

    return (
        <div>
            <CreateLessonPageBlocks
                // lessonId берется из URL, поэтому редактирование урока/блоков корректно включается
                pageTitleCreate="Создать урок"
                pageTitleEdit="Редактировать урок-материал"
                pageSubtitle="Урок-материал и назначение студентам"
                cancelNavigateTo="/admin/materials"
                afterSaveNavigateTo="/admin/materials"
                renderExtraParams={renderExtraParams}
                onAfterLessonUpdated={async ({ lessonId: savedLessonId, lessonTitle, lessonDescription }) => {
                    if (visibility === 'students' && selectedStudentIds.length === 0 && selectedGroupIds.length === 0) {
                        throw new Error('Выберите хотя бы одного ученика или группу');
                    }

                    setLoading(true);

                    const formData = new FormData();
                    formData.append('title', lessonTitle || '');
                    formData.append('description', lessonDescription || '');
                    formData.append('type', 'lesson');
                    formData.append('lesson_id', savedLessonId || lessonId);
                    formData.append('visibility', visibility);
                    formData.append('studentIds', JSON.stringify(selectedStudentIds));
                    formData.append('groupIds', JSON.stringify(selectedGroupIds));

                    await materialsAPI.updateMaterial(materialId, formData);
                    setLoading(false);
                }}
            />

            {loading && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                    <div style={{ background: 'white', padding: '20px 30px', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.15)' }}>
                        Сохранение...
                    </div>
                </div>
            )}
        </div>
    );
};

export default EditLessonMaterialPage;

